import { useState, useEffect, useMemo } from 'react';
import { updateProjectStep6, getProject, getPricing } from '../../services/api';

// States that support bulky waste room sizing (exclude NSW and NT)
const BULKY_WASTE_STATES = ['ACT', 'QLD', 'WA', 'SA', 'TAS', 'VIC'];

const Step6RefinementsRecommendations = ({ project, onComplete, onBack, selectedAddons, setSelectedAddons }) => {
  const [localSelectedAddons, setLocalSelectedAddons] = useState(selectedAddons || []);
  const [saving, setSaving] = useState(false);
  const [bulkyWasteRoomSize, setBulkyWasteRoomSize] = useState('');
  const [useCustomSize, setUseCustomSize] = useState(false);
  const [addonsConfig, setAddonsConfig] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  // Fetch add-ons pricing configuration
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const pricing = await getPricing();
        setAddonsConfig(pricing.addons || {});
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Fallback defaults
        setAddonsConfig({
          compliance_check: { name: "Council Compliance Review and WMP Refinement", price: 120 },
          rfi_response: { name: "Council Request for Information (RFI) Response Support", price: 150 },
          extra_30_day: { name: "Additional 30 Days PlanGen Access", price: 100 },
          bin_storage_review: { name: "Waste Room / Bin Storage Layout Review", price: 80 }
        });
      } finally {
        setLoadingPricing(false);
      }
    };
    fetchPricing();
  }, []);

  // Calculate default bulky waste room size based on dwelling count and state
  const calculatedBulkyWasteSize = useMemo(() => {
    const stateCode = project?.state_code || '';
    const dwellingCount = project?.step2_data?.dwelling_count || 0;
    
    // WA has a 12m² minimum requirement
    if (stateCode === 'WA') {
      return 12;
    }
    
    // Default best-practice sizing based on dwelling count
    if (dwellingCount <= 20) {
      return 6;
    } else if (dwellingCount <= 50) {
      return 9; // Midpoint of 8-10 m²
    } else {
      // Scale proportionally for larger developments
      const additionalArea = (dwellingCount - 50) * 0.15;
      return Math.round((10 + additionalArea) * 10) / 10;
    }
  }, [project]);

  // Check if this state supports bulky waste room sizing
  const showBulkyWasteSection = useMemo(() => {
    return BULKY_WASTE_STATES.includes(project?.state_code);
  }, [project?.state_code]);

  // Initialize bulky waste room size from project or calculated value
  useEffect(() => {
    const existingSize = project?.step6_data?.bulky_waste_room_size;
    if (existingSize && existingSize !== calculatedBulkyWasteSize) {
      setBulkyWasteRoomSize(existingSize.toString());
      setUseCustomSize(true);
    } else {
      setBulkyWasteRoomSize(calculatedBulkyWasteSize.toString());
      setUseCustomSize(false);
    }
  }, [project, calculatedBulkyWasteSize]);

  // Initialize addons from project data or passed props
  useEffect(() => {
    if (project?.selected_addons && project.selected_addons.length > 0) {
      setLocalSelectedAddons(project.selected_addons);
    } else if (selectedAddons && selectedAddons.length > 0) {
      setLocalSelectedAddons(selectedAddons);
    }
  }, [project, selectedAddons]);

  const toggleAddon = (addonKey) => {
    setLocalSelectedAddons(prev => {
      if (prev.includes(addonKey)) {
        return prev.filter(k => k !== addonKey);
      } else {
        return [...prev, addonKey];
      }
    });
  };

  const calculateAddonsTotal = () => {
    if (!addonsConfig) return 0;
    return localSelectedAddons.reduce((total, key) => {
      return total + (addonsConfig[key]?.price || 0);
    }, 0);
  };

  const handleContinue = async () => {
    try {
      setSaving(true);
      setSelectedAddons(localSelectedAddons);
      
      // Prepare Step 6 data
      const step6Data = {
        selected_addons: localSelectedAddons
      };
      
      // Only include bulky waste room size if custom value is used
      if (showBulkyWasteSection && useCustomSize && bulkyWasteRoomSize) {
        step6Data.bulky_waste_room_size = parseFloat(bulkyWasteRoomSize);
      }
      
      // Save Step 6 data to backend
      await updateProjectStep6(project.id, step6Data);
      
      // Fetch updated project with correct current_step
      const updatedProject = await getProject(project.id);
      onComplete(updatedProject);
    } catch (error) {
      console.error('Failed to save Step 6:', error);
      // Still try to continue even if save fails
      onComplete({ ...project, selected_addons: localSelectedAddons });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToCalculated = () => {
    setBulkyWasteRoomSize(calculatedBulkyWasteSize.toString());
    setUseCustomSize(false);
  };

  const handleCustomSizeChange = (e) => {
    const value = e.target.value;
    setBulkyWasteRoomSize(value);
    if (value && parseFloat(value) !== calculatedBulkyWasteSize) {
      setUseCustomSize(true);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Before You Download Your Waste Management Plan</h2>
        <p className="mt-1 text-sm text-gray-600">
          Important information about the assessment process and optional support services.
        </p>
      </div>

      <div className="px-8 py-6 space-y-8">
        
        {/* What to Expect Section */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-blue-900">What to Expect</h3>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-gray-700">
              Waste Management Plans are assessment documents, not static checklists. Even well-prepared plans may receive council comments or requests for clarification during assessment — <strong>this is normal</strong>.
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Local councils may request:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Clarification on bin sizes or collection arrangements
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Confirmation of waste volumes or servicing assumptions
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Minor wording changes to align with local policies
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Adjustments following design changes to the development
                </li>
              </ul>
            </div>
            <p className="text-sm text-gray-600 italic">
              These requests are a standard part of the development assessment process, not a rejection of your plan.
            </p>
          </div>
        </div>

        {/* How This Plan Helps Section */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-green-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-green-900">How This Plan Helps You</h3>
          </div>
          <div className="p-4">
            <p className="text-gray-700 mb-3">
              Your Waste Management Plan is structured to respond confidently to council feedback:
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center bg-gray-50 rounded-lg p-3">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700">Current best-practice principles</span>
              </div>
              <div className="flex items-center bg-gray-50 rounded-lg p-3">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700">Accepted infrastructure standards</span>
              </div>
              <div className="flex items-center bg-gray-50 rounded-lg p-3">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700">Flexible, council-friendly language</span>
              </div>
              <div className="flex items-center bg-gray-50 rounded-lg p-3">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700">Format familiar to assessors</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Most council requests can be addressed through minor updates or clarifications — not a complete rework.
            </p>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h4 className="text-sm font-semibold text-amber-800">Important Notice</h4>
              <p className="mt-1 text-sm text-amber-700">
                This Waste Management Plan is prepared based on the information provided at the time of generation. 
                If the development design, use, or waste services change, the plan may need to be updated accordingly. 
                This is standard across all consultant-prepared WMPs.
              </p>
            </div>
          </div>
        </div>

        {/* Bulky Waste Room Size Section - Only show for applicable states */}
        {showBulkyWasteSection && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Bulky Waste Room Size</h3>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                A dedicated bulky waste storage area is required for multi-unit developments to accommodate 
                furniture, mattresses, and other large items between collections.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">
                    Calculated Size (based on {project?.step2_data?.dwelling_count || 0} dwelling units):
                  </span>
                  <span className="text-lg font-bold text-blue-700">{calculatedBulkyWasteSize} m²</span>
                </div>
                {project?.state_code === 'WA' && (
                  <p className="text-xs text-blue-700">
                    WA councils typically require a minimum of 12 m² for bulky waste storage.
                  </p>
                )}
                {project?.state_code !== 'WA' && (
                  <p className="text-xs text-blue-700">
                    Best-practice sizing: ≤20 units = 6m², 21-50 units = 9m², &gt;50 units = scaled proportionally.
                  </p>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label htmlFor="bulkyWasteSize" className="text-sm font-medium text-gray-900">
                    Override Size (optional):
                  </label>
                  {useCustomSize && (
                    <button
                      type="button"
                      onClick={handleResetToCalculated}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Reset to calculated
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    id="bulkyWasteSize"
                    value={bulkyWasteRoomSize}
                    onChange={handleCustomSizeChange}
                    min="4"
                    max="100"
                    step="0.5"
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-sm text-gray-600">m²</span>
                  {useCustomSize && (
                    <span className="text-xs text-amber-600 ml-2">
                      (Custom value - will override calculated size in PDF)
                    </span>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  If your design includes a different bulky waste room size, enter it here. 
                  This value will appear in Section 5.2 of your Waste Management Plan.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* RFI Response Support Add-On */}
        <div className="border-2 border-blue-200 rounded-lg overflow-hidden bg-blue-50">
          <div className="bg-blue-100 px-4 py-3 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-blue-900">Optional Add-On Services</h3>
              {localSelectedAddons.length > 0 && (
                <span className="text-lg font-bold text-blue-700">
                  Total: ${calculateAddonsTotal().toFixed(2)}
                </span>
              )}
            </div>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-gray-700 mb-4">
              Enhance your waste management plan with these optional services.
            </p>

            {loadingPricing ? (
              <div className="text-center py-4">Loading add-on options...</div>
            ) : (
              <div className="space-y-4">
                {/* Compliance Check - FIRST */}
                {addonsConfig?.compliance_check && (
                  <div className={`border-2 rounded-lg overflow-hidden transition-all ${
                    localSelectedAddons.includes('compliance_check') 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 bg-white hover:border-blue-300'
                  }`}>
                    <div className="p-4">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={localSelectedAddons.includes('compliance_check')}
                          onChange={() => toggleAddon('compliance_check')}
                          className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">{addonsConfig.compliance_check.name}</span>
                            <span className="font-bold text-blue-700">+${addonsConfig.compliance_check.price.toFixed(2)}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {addonsConfig.compliance_check.description}
                          </p>
                          {localSelectedAddons.includes('compliance_check') && addonsConfig.compliance_check.includes && (
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                              {addonsConfig.compliance_check.includes.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <svg className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* RFI Response Support */}
                {addonsConfig?.rfi_response && (
                  <div className={`border-2 rounded-lg overflow-hidden transition-all ${
                    localSelectedAddons.includes('rfi_response') 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 bg-white hover:border-blue-300'
                  }`}>
                    <div className="p-4">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={localSelectedAddons.includes('rfi_response')}
                          onChange={() => toggleAddon('rfi_response')}
                          className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">{addonsConfig.rfi_response.name}</span>
                            <span className="font-bold text-blue-700">+${addonsConfig.rfi_response.price.toFixed(2)}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            Council requests for information are common. If council requests clarification or amendments, 
                            this add-on provides targeted assistance.
                          </p>
                          {localSelectedAddons.includes('rfi_response') && (
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                              {addonsConfig.rfi_response.includes?.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <svg className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Extra 30 Day */}
                {addonsConfig?.extra_30_day && (
                  <div className={`border-2 rounded-lg overflow-hidden transition-all ${
                    localSelectedAddons.includes('extra_30_day') 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 bg-white hover:border-blue-300'
                  }`}>
                    <div className="p-4">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={localSelectedAddons.includes('extra_30_day')}
                          onChange={() => toggleAddon('extra_30_day')}
                          className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">{addonsConfig.extra_30_day.name}</span>
                            <span className="font-bold text-blue-700">+${addonsConfig.extra_30_day.price.toFixed(2)}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {addonsConfig.extra_30_day.description}
                          </p>
                          {localSelectedAddons.includes('extra_30_day') && addonsConfig.extra_30_day.includes && (
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                              {addonsConfig.extra_30_day.includes.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <svg className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Bin Storage Review */}
                {addonsConfig?.bin_storage_review && (
                  <div className={`border-2 rounded-lg overflow-hidden transition-all ${
                    localSelectedAddons.includes('bin_storage_review') 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 bg-white hover:border-blue-300'
                  }`}>
                    <div className="p-4">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={localSelectedAddons.includes('bin_storage_review')}
                          onChange={() => toggleAddon('bin_storage_review')}
                          className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">{addonsConfig.bin_storage_review.name}</span>
                            <span className="font-bold text-blue-700">+${addonsConfig.bin_storage_review.price.toFixed(2)}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {addonsConfig.bin_storage_review.description}
                          </p>
                          {localSelectedAddons.includes('bin_storage_review') && addonsConfig.bin_storage_review.includes && (
                            <ul className="text-sm text-gray-600 mt-2 space-y-1">
                              {addonsConfig.bin_storage_review.includes.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <svg className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            <p className="mt-3 text-xs text-gray-500 italic">
              Note: Council assessment processes vary, and final decisions remain at the discretion of the approving authority. 
              These services assist with preparation and response but cannot guarantee approval outcomes.
            </p>
          </div>
        </div>

        {/* Summary if add-ons selected */}
        {localSelectedAddons.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-green-800 font-medium">
                  {localSelectedAddons.length} add-on{localSelectedAddons.length > 1 ? 's' : ''} selected
                </span>
              </div>
              <span className="text-green-800 font-bold">
                +${calculateAddonsTotal().toFixed(2)}
              </span>
            </div>
            <ul className="mt-2 ml-7 text-sm text-green-700 space-y-1">
              {localSelectedAddons.map(key => (
                <li key={key}>• {addonsConfig?.[key]?.name || key}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={saving}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Continue to Supporting Docs'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step6RefinementsRecommendations;
